<template>
  <b-overlay
    :show="loader.form"
    rounded="sm"
  >
    <!-- Datos básicos de un cupón -->
    <validation-observer ref="formCupon">
      <b-card class="blog-edit-wrapper">
        <titleSection
          v-if="!isEmptyObject(cupon)"
          :title="(cupon.codigo) ? cupon.codigo : 'Ingrese cupón'"
          icon="TagIcon"
        />
        <!-- Información de cupón -->
        <b-form
          v-if="!isEmptyObject(cupon)"
          class="mt-2"
        >
          <b-row>
            <b-col md="6">
              <b-form-group
                label="Código"
                label-for="codigo"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="código"
                  rules="required|max:10|codigoCupon"
                >
                  <b-form-input
                    id="codigo"
                    v-model="cupon.codigo"
                    placeholder="Código del cupón"
                    :state="errors.length > 0 ? false:null"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group
                label="Capacidad"
                label-for="capacidad"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="capacidad"
                  rules="required|positive"
                >
                  <b-form-input
                    id="capacidad"
                    v-model="cupon.capacidad"
                    placeholder="N°"
                    :state="errors.length > 0 ? false:null"
                    type="number"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group
                label="Descuento"
                label-for="descuento"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="descuento"
                  rules="required|between:1,100"
                >
                  <b-form-input
                    id="descuento"
                    v-model="cupon.descuento"
                    placeholder="1% al 100%"
                    :state="errors.length > 0 ? false:null"
                    type="number"
                    max="100"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>

        <!-- Botones de cancelación y guardado -->
        <div class="text-right">
          <!-- Save changes -->
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            @click.prevent="validationForm"
          >
            Guardar cambios
          </b-button>
          <!-- Cancel -->
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-danger"
            @click="(id) ? $emit('update:id', null) : $router.push('/servicio-escolar/dashboard')"
          >
            Cancelar
          </b-button>
        </div>
      </b-card>
    </validation-observer>
  </b-overlay>
</template>

<script>
import {
  BCard,
  BOverlay,
  BForm,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import titleSection from '@/views/components/title-section.vue'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ref, onMounted, computed } from '@vue/composition-api'
import store from '@/store'
import { isEmptyObject } from '@core/utils/utils'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required } from '@validations'
import { cuponInfo } from '../useCupones'

export default {
  components: {
    BCard,
    BOverlay,
    BForm,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    titleSection,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  props: {
    id: {
      type: Number,
      default: null,
    },
    codigorules: {
      type: [Object, String],
      default: '',
    },
  },
  setup(props, { emit }) {
    const toast = useToast()
    const cupon = ref({})
    const loader = ref({ form: false })
    const formCupon = ref(null)

    //* Computed method to get form values
    const getValuesToSend = computed(() => {
      const {
        codigo, capacidad, descuento,
      } = cupon.value

      return {
        codigo, capacidad, descuento,
      }
    })
    //* Set código data as an empty object
    const newCuponData = () => {
      cupon.value = {
        codigo: null,
        capacidad: null,
        descuento: null,
      }
    }
    //* Save changes
    const saveChanges = () => {
      const toUpdate = getValuesToSend.value
      loader.value.form = true
      const data = {
        task: 'wscupones',
        action: props.id ? 3 : 4, //* Si hay id es edición [3], sino nuevo item [4]
        data: JSON.stringify(toUpdate),
      }
      if (props.id !== undefined && props.id) data.id = props.id //* Si existe id, envío plan_estudios [Es edición]
      store.dispatch('servicioEscolar/postServicioEscolar', data)
        .then(response => {
          loader.value.form = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Solicitud exitosa',
              text: response.message,
              icon: 'CheckIcon',
              variant: 'success',
            },
          },
          { timeout: 8000 })
          if (!props.id) {
            newCuponData()
            emit('refetch-data')
          }
        }, error => {
          loader.value.form = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error en petición [Actualizar cupón]',
              text: error,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          },
          { timeout: 10000 })
        })
    }

    //* Load cupon data
    const loadCuponData = () => {
      loader.value.form = true
      cuponInfo(props.id).then(response => {
        loader.value.form = false
        cupon.value = response
      }, error => {
        loader.value.form = false
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error en petición [Información del cupón]',
            text: error,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        },
        { timeout: 10000 })
      })
    }

    extend('codigoCupon', {
      message:
    'El {_field_} solo admite letras sin acento y números; no están permitidos los espacios.',
      validate: value => {
        const cuponvalido = new RegExp(/^[A-Za-z0-9]+$/g)
        return cuponvalido.test(value)
      },
    })

    onMounted(
      () => {
        if (props.id) loadCuponData() //* If we have an id, so component will edit, get data
        else newCuponData() //* Will create a new item, so put empty data
      },
    )

    const validationForm = () => {
      formCupon.value.validate().then(success => {
        if (success) saveChanges()
      })
    }
    return {
      loadCuponData,
      saveChanges,
      loader,
      cupon,
      isEmptyObject,
      required,
      formCupon,
      validationForm,
    }
  },
}
</script>
